import { useSyncExternalStore } from 'react';

/**
 * Whether the platform is in a maintenance window right now, as far as this
 * tab knows.
 *
 * Fed from two directions, because either can be first to find out:
 *
 * - the notices poll (`PlatformNoticeBanner`, `MaintenanceScreen`), which says
 *   so ahead of anything failing, and is the only thing that says it is over;
 * - the API client, which sees a `503 MAINTENANCE` the moment a request is
 *   refused - the poll may be a minute behind.
 *
 * A dependency-free leaf on purpose: `api/client.ts` imports it, so it must not
 * import anything that imports the client back.
 */

export type MaintenanceMode = 'READ_ONLY' | 'FULL';

export interface MaintenanceState {
  mode: MaintenanceMode;
  /** An estimate. Past it, copy says "shortly" rather than a time already gone. */
  endsAt: string | null;
  message: string | null;
}

let current: MaintenanceState | null = null;
const listeners = new Set<() => void>();

function same(a: MaintenanceState | null, b: MaintenanceState | null): boolean {
  if (a === null || b === null) return a === b;
  return a.mode === b.mode && a.endsAt === b.endsAt && a.message === b.message;
}

export function getMaintenance(): MaintenanceState | null {
  return current;
}

export function setMaintenance(next: MaintenanceState | null): void {
  if (same(current, next)) return;
  current = next;
  for (const listener of [...listeners]) listener();
}

export function subscribeMaintenance(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current window, re-rendering when it starts, changes or ends. */
export function useMaintenance(): MaintenanceState | null {
  return useSyncExternalStore(subscribeMaintenance, getMaintenance, getMaintenance);
}

function isMode(value: unknown): value is MaintenanceMode {
  return value === 'READ_ONLY' || value === 'FULL';
}

/**
 * The state a `MAINTENANCE` error envelope describes. A refusal with no
 * readable mode is treated as FULL: the server refused a request, and that is
 * the only thing certain about it.
 */
export function maintenanceFromDetails(
  details: Record<string, unknown> | null,
  message: string | null,
): MaintenanceState {
  const mode = details?.mode;
  const endsAt = details?.ends_at;
  return {
    mode: isMode(mode) ? mode : 'FULL',
    endsAt: typeof endsAt === 'string' ? endsAt : null,
    message,
  };
}

// --- Refusals ---------------------------------------------------------------

/**
 * How long after a refusal other error toasts are held back.
 *
 * Pages already toast their own mutation failures ("Could not save" with the
 * server's reason). During read-only maintenance that would land beside the
 * friendly "saving is paused" toast saying the same thing less helpfully, so
 * for a moment after a refusal the friendly one is the only one shown.
 */
const QUIET_MS = 3_000;
/** One friendly toast per burst: a page saving five things gets one, not five. */
const TOAST_GAP_MS = 4_000;

let quietUntil = 0;
let lastToastAt = Number.NEGATIVE_INFINITY;
const refusalListeners = new Set<(state: MaintenanceState) => void>();

/**
 * The API client saw a `503 MAINTENANCE`. Records the state and, in read-only
 * mode, asks whoever is listening to say so once.
 */
export function reportMaintenanceRefusal(state: MaintenanceState): void {
  setMaintenance(state);
  if (state.mode !== 'READ_ONLY') return;
  const now = Date.now();
  quietUntil = now + QUIET_MS;
  if (now - lastToastAt < TOAST_GAP_MS) return;
  lastToastAt = now;
  for (const listener of [...refusalListeners]) listener(state);
}

/** Called once per burst of read-only refusals; layouts turn it into a toast. */
export function onMaintenanceRefusal(listener: (state: MaintenanceState) => void): () => void {
  refusalListeners.add(listener);
  return () => {
    refusalListeners.delete(listener);
  };
}

/** True just after a read-only refusal, while a page's own error toast would be a duplicate. */
export function inMaintenanceQuietWindow(): boolean {
  return Date.now() < quietUntil;
}

/** Tests only: forget everything this module has been told. */
export function resetMaintenanceForTests(): void {
  current = null;
  quietUntil = 0;
  lastToastAt = Number.NEGATIVE_INFINITY;
  for (const listener of [...listeners]) listener();
}

// --- Wording helpers --------------------------------------------------------

/** Maintenance times are announced in IST, the platform's own clock. */
export const MAINTENANCE_ZONE = 'Asia/Kolkata';

/** `03:00 IST`, or null when there is no estimate or it has already passed. */
export function maintenanceTime(iso: string | null, now = Date.now()): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime()) || at.getTime() <= now) return null;
  return `${clock(at)} IST`;
}

function clock(at: Date): string {
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: MAINTENANCE_ZONE,
  }).format(at);
}

function weekday(at: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: MAINTENANCE_ZONE }).format(at);
}

function dayKey(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: MAINTENANCE_ZONE }).format(at);
}

/** `Sat 02:00–03:00 IST`, `Sat 23:00 – Sun 01:00 IST`, or `Sat 02:00 IST` with no end. */
export function maintenanceWindowLabel(
  startsAt: string,
  endsAt: string | null,
  locale = 'en-IN',
): string {
  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : null;
  const head = `${weekday(start, locale)} ${clock(start)}`;
  if (!end || Number.isNaN(end.getTime())) return `${head} IST`;
  if (dayKey(start) === dayKey(end)) return `${head}–${clock(end)} IST`;
  return `${head} – ${weekday(end, locale)} ${clock(end)} IST`;
}

/** Time left until `iso`, split for wording; null once it has arrived. */
export function countdownParts(
  iso: string,
  now = Date.now(),
): { days: number; hours: number; minutes: number } | null {
  const left = new Date(iso).getTime() - now;
  if (!Number.isFinite(left) || left <= 0) return null;
  // Rounded up, so "starts in 0m" never shows while it is still to come.
  const totalMinutes = Math.ceil(left / 60_000);
  return {
    days: Math.floor(totalMinutes / 1_440),
    hours: Math.floor((totalMinutes % 1_440) / 60),
    minutes: totalMinutes % 60,
  };
}
